import AxiosFoundationAPI from "../../../../util/AxiosFoundationAPI";
import {V1ObjectRelationshipIDOCToArray} from "../../../../util/Payload";
import EnterpriseComponent from "../../EnterpriseComponent";
import {v4 as uuidv4} from "uuid";

// Typed edges between two logical objects (t1/v1/object_relationships).
const IDOC = "v1.ObjectRelationship-001";
const URL = "t1/v1/object_relationships";

class ObjectRelationshipEnterpriseComponent extends EnterpriseComponent {

    performQuery = (page, pageSize, foreignKey) => {
        page = page || 1;
        pageSize = pageSize || this.getDefaultCacheSize();

        this.notifyLoadStartListeners();

        let idocResponse = this.getIDOCResponse() ?? IDOC;

        let members = [];
        let queryPayload = this.prepareQueryPayload();
        if (queryPayload.size > 0) {
            for (const [key, value] of queryPayload) {
                if (key.includes("_query")) continue;
                let operator = "EQUAL";
                if (queryPayload.get(key + "_query") !== undefined) operator = queryPayload.get(key + "_query");
                members.push({"Type": "Attribute", "Name": key, "Value": value, "Operator": operator});
            }
        }

        let QBE = {
            "PlatformMessage": {
                "-idoc": idocResponse,
                "-id": uuidv4(),
                "ObjectRelationship": {
                    "_attributes": {"limit": pageSize, "sort": this.getFormattedSortSpecification(), "offset": (page - 1) * pageSize},
                    "QueryGroups": {"QueryGroup": [{"Name": "primary", "Operator": "AND", "Type": "QueryGroup", "Membership": {"Member": members}}]}
                }
            }
        };

        AxiosFoundationAPI({
            method: "GET",
            url: URL,
            json: true,
            params: {"QBE": btoa(JSON.stringify(QBE))},
            headers: {
                "visibility-mode": this.getVisibilityMode(),
                "visibility-value": this.getVisibilityValue(),
                "idoc-response": idocResponse,
            },
        }).then(response => {
            this.fetchEnterpriseComponentSuccess(V1ObjectRelationshipIDOCToArray(response.data), page, pageSize);
        }).catch(error => {
            this.fetchEnterpriseComponentFailed(error);
        });
    };

    buildObjectRelationshipPayload = () => {
        let r = {};
        r["USID"] = this.getAttributeValue("USID");
        // FALLS BACK TO THE SCOPE, because a new edge created from a record's grid belongs to that
        // record and nothing in the dialog asks for it. newRecord() applies defaults, foreign keys
        // and the realm but NOT the scope, so without this the parent arrives empty and the write is
        // rejected -- or worse, accepted against nothing. Reading it here means the answer does not
        // depend on when the dialog happened to populate its controls.
        r["ParentUSID"] = this.getAttributeValue("ParentUSID")
            || (this.getScope && this.getScope().get ? this.getScope().get("ParentUSID") : null);
        r["ChildUSID"] = this.getAttributeValue("ChildUSID");
        r["ObjectRelationshipTypeUSID"] = this.getAttributeValue("ObjectRelationshipTypeUSID");

        // THE PIN IS SENT ONLY WHEN IT WAS ASKED FOR. The chooser captures the revision it was
        // picked by on every selection, so the value is sitting there whether or not the reader
        // wants a pin -- sending it unconditionally would pin every new relationship, which is the
        // exact opposite of the default. An explicit null on the floating path, not an omission,
        // because unpinning an existing edge has to be expressible too.
        r["ChildPinnedRevisionUSID"] = this.getAttributeValue("PinToRevision") === true
            ? (this.getAttributeValue("ChildPickedRevisionUSID") || this.getAttributeValue("ChildPinnedRevisionUSID"))
            : null;
        return r;
    };

    enterpriseComponentPostIDOC = () => {
        let payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ObjectRelationship = this.buildObjectRelationshipPayload();
        return payload;
    };

    performInsert() {
        let payload = this.enterpriseComponentPostIDOC();
        let options = {method: "POST", url: URL, json: true, data: payload, headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyInsertStartListeners();
        AxiosFoundationAPI(options)
            .then(response => { this.insertEnterpriseComponentSuccess(V1ObjectRelationshipIDOCToArray(response.data)); })
            .catch(error => { this.insertEnterpriseComponentFailed(error); });
    };

    enterpriseComponentPutIDOC = () => {
        let payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ObjectRelationship = this.preparePUTPayload();
        return payload;
    };

    performUpdate() {
        let payload = this.enterpriseComponentPutIDOC();
        let options = {method: "PUT", url: URL, json: true, data: payload, headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyUpdateStartListeners();
        AxiosFoundationAPI(options)
            .then(response => { this.updateEnterpriseComponentSuccess(V1ObjectRelationshipIDOCToArray(response.data)); })
            .catch(error => { this.updateEnterpriseComponentFailed(error); });
    };

    performDelete() {
        let payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ObjectRelationship = {"USID": this.getAttributeValue("USID")};
        let options = {method: "DELETE", url: URL, json: true, data: payload, headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyDeleteStartListeners();
        AxiosFoundationAPI(options)
            .then(() => { this.deleteEnterpriseComponentSuccess(); })
            .catch(error => { this.deleteEnterpriseComponentFailed(error); });
    };
}

export default ObjectRelationshipEnterpriseComponent;
